package diana.soleil.design.iraniancanadienbazzar.navigation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import diana.soleil.design.iraniancanadienbazzar.core.common.AppBuildInfo
import diana.soleil.design.iraniancanadienbazzar.core.data.ConnectivityController
import diana.soleil.design.iraniancanadienbazzar.core.data.SunsetConfig
import diana.soleil.design.iraniancanadienbazzar.core.data.fake.MockDataController
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBanner
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.locale.LocaleController
import iraniancanadienbazzar.shared.generated.resources.Res
import iraniancanadienbazzar.shared.generated.resources.mock_data_banner
import iraniancanadienbazzar.shared.generated.resources.offline_banner
import iraniancanadienbazzar.shared.generated.resources.prod_preview_banner
import iraniancanadienbazzar.shared.generated.resources.sunset_banner_default
import iraniancanadienbazzar.shared.generated.resources.sunset_dismiss_a11y
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

/**
 * The app-wide status banner stack — prod-preview guardrail, mock-data guardrail, graceful-sunset,
 * and offline. Hoisted ABOVE the nav host (App.kt) so pushed routes (business/chat/notification
 * detail screens) show them too, not just the 5 tabs (PENDING §2 follow-up: the offline + MOCK
 * banners were invisible on detail screens). Returns whether anything is showing so the shell can
 * consume the status-bar inset (the visible banner already occupies that zone — without consuming,
 * every screen's own statusBarsPadding would double-pad).
 */
@Composable
fun AppStatusBanners(buildInfo: AppBuildInfo): Boolean {
    var anyShown = false

    // PROD-PREVIEW guardrail: a debug build pointed at the real prod backend (prodDebug / web
    // ?env=prod) — persistent, non-dismissible red bar. Never in normal dev or shipped release.
    if (buildInfo.isDebug && buildInfo.firebaseEnv == "prod") {
        anyShown = true
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.error)
                .statusBarsPadding()
                .padding(vertical = AppTheme.spacing.xs),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = stringResource(Res.string.prod_preview_banner),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onError,
                textAlign = TextAlign.Center,
            )
        }
    }

    // MOCK-DATA guardrail: everything on screen is in-memory demo data. Debug-only by construction.
    val mockDataOn by MockDataController.enabled.collectAsState()
    // Store-screenshot capture keeps the mock data but drops the banner (it would sit in every
    // marketing image). Only a debug/simulator launch switch can set it — see MockDataController.
    val screenshotMode by MockDataController.screenshotMode.collectAsState()
    if (buildInfo.isDebug && mockDataOn && !screenshotMode) {
        val mockModifier = if (anyShown) Modifier else Modifier.statusBarsPadding()
        anyShown = true
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(AppTheme.colors.secondary)
                .then(mockModifier)
                .padding(vertical = AppTheme.spacing.xs),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = stringResource(Res.string.mock_data_banner),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSecondary,
                textAlign = TextAlign.Center,
            )
        }
    }

    // Graceful-sunset banner: RC-driven; dismissible per launch (in-memory state).
    val sunsetConfig = koinInject<SunsetConfig>()
    val sunset by sunsetConfig.state.collectAsState()
    val locale = koinInject<LocaleController>()
    val language by locale.language.collectAsState()
    var sunsetDismissed by remember { mutableStateOf(false) }
    if (sunset.enabled && !sunsetDismissed) {
        val message = sunset.message.resolve(language)
            .ifBlank { stringResource(Res.string.sunset_banner_default) }
        val insetModifier = if (anyShown) Modifier else Modifier.statusBarsPadding()
        anyShown = true
        Box(modifier = insetModifier) {
            AppBanner(
                message = message,
                onDismiss = { sunsetDismissed = true },
                dismissContentDescription = stringResource(Res.string.sunset_dismiss_a11y),
            )
        }
    }

    // Offline banner: not dismissible — clears itself when connectivity returns.
    val connectivityController = koinInject<ConnectivityController>()
    val isOnline by connectivityController.isOnline.collectAsState(initial = true)
    if (!isOnline) {
        val insetModifier = if (anyShown) Modifier else Modifier.statusBarsPadding()
        anyShown = true
        Box(modifier = insetModifier) {
            AppBanner(
                message = stringResource(Res.string.offline_banner),
                iconName = "wifi_off",
            )
        }
    }

    return anyShown
}
